//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled, { css } from 'styled-components';

import DetailsSheet from '@components/molecules/details-sheet/DetailsSheet';
import { Mixins } from '@theme/mixins';
import { isDefined } from '@utils/filterPredicates';
import {
  ReadableNumberSignOption,
  optionalReadablePrice,
  readableNumber,
  readableRate,
} from '@utils/readableNumber';
import { ITrackableComponent, withTracking } from '@utils/withTracking';

interface IBaseMetric {
  value?: number;
  secondaryValue?: string;
  label: string;
  tooltip?: string;
}

interface IValueMetric extends IBaseMetric {
  /**
   * Whether to display a +/- sign.
   * Defaults to only display when the value is negative.
   */
  displaySignIf?: ReadableNumberSignOption;
}

interface IRateMetric extends IBaseMetric {
  /**
   * Whether to display a +/- sign.
   * Defaults to only display when the value is negative.
   */
  displaySignIf?: ReadableNumberSignOption;
}

interface IPriceMetric extends IBaseMetric {
  /**
   * The currency this value is in, for monetary currencies.
   * e.g. 'USD'.
   *
   * Defaults to USD.
   */
  currencyFormat?: string;
}

interface OptionalTooltipProps {
  children?: React.ReactNode;
  tooltip?: string;
}

const OptionalTooltip = ({ children, tooltip }: OptionalTooltipProps) => {
  if (tooltip) {
    return (
      <DetailsSheet sheetContent={<div>{tooltip}</div>}>
        <div>{children}</div>
      </DetailsSheet>
    );
  }
  return <>{children}</>;
};

// Shorten boilerplate of creating the Metric component props
type MetricProps<T> = React.HTMLAttributes<HTMLDivElement> &
  ITrackableComponent &
  T;

// Replace the `value` prop with a string on the base metric.
type BaseMetricProps = MetricProps<
  Omit<IBaseMetric, 'value'> & { value: string }
>;

export const BaseMetric = ({
  label,
  secondaryValue,
  tooltip,
  trackingId,
  value,
  ...restProps
}: BaseMetricProps) => {
  return (
    <MetricWrapper trackingId={trackingId} {...restProps}>
      <OptionalTooltip tooltip={tooltip}>{label}</OptionalTooltip>
      <ValueWrapper>
        <Value>{value}</Value>
        {secondaryValue && <SecondaryValue>({secondaryValue})</SecondaryValue>}
      </ValueWrapper>
    </MetricWrapper>
  );
};

export const ValueMetric = ({
  displaySignIf,
  value,
  ...restProps
}: MetricProps<IValueMetric>) => (
  <BaseMetric
    value={
      typeof value === 'number'
        ? readableNumber(value, {
            sign: displaySignIf ?? ReadableNumberSignOption.NEGATIVE,
          })
        : '--'
    }
    {...restProps}
  />
);

export const RateMetric = ({
  displaySignIf,
  value,
  ...restProps
}: MetricProps<IRateMetric>) => (
  <BaseMetric
    value={
      isDefined(value)
        ? readableRate(value, {
            rateDecimals: 1,
            rateIncludePercentSuffix: true,
            sign: displaySignIf,
          })
        : '--'
    }
    {...restProps}
  />
);

/**
 * Render a metric value representing a price.
 *
 * Optionally specify the currency to be used. Defaults to USD.
 */
export const PriceMetric = ({
  currencyFormat,
  value,
  ...restProps
}: MetricProps<IPriceMetric>) => (
  <BaseMetric
    value={optionalReadablePrice(value, false, currencyFormat)}
    {...restProps}
  />
);

const MetricWrapper = withTracking(styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${Mixins.Text.BodySecondary}
`);

const ValueWrapper = styled.div`
  display: flex;
`;

const Value = styled.div`
  ${Mixins.Text.BodyBold}
  word-break: keep-all;
`;

const SecondaryValue = styled.div(
  ({ theme }) => `
  ${Mixins.Text.Body}
  margin-left: ${theme.spacings[1]};
`,
);

export const MetricList = styled.div<{ isExportReport?: boolean }>(
  ({ isExportReport = false, theme }) => css`
    ${MetricWrapper}:not(:first-of-type) {
      margin-top: ${isExportReport ? 0 : theme.spacings[2]};
    }
  `,
);
